import { api } from './client';
import { i18nMap, i18nMetaMap } from '@/lib/i18n-map';

export interface LeaveRequest {
  id: string;
  staffId: string;
  staffName: string | null;
  kind: 'concediu' | 'medical' | 'neplatit' | 'special';
  from: string;
  to: string;
  days: number;
  halfDay: boolean;
  reason: string | null;
  status: 'ceruta' | 'aprobata' | 'respinsa' | 'anulata';
  requestedBy: string | null;
  requestedAt: string;
  decidedBy: string | null;
  decidedAt: string | null;
  decisionNote: string | null;
}

export interface LeaveBalance {
  year: number;
  entitled: number;
  used: number;
  remaining: number;
  annualAllowance: number;
  /** A cui e norma: scrisă pe fișa omului, sau a clinicii. */
  allowanceSource: 'staff' | 'clinic';
}

export interface AttendanceDay {
  id: string;
  staffId: string;
  workDate: string;
  checkIn: string | null;
  checkOut: string | null;
  hours: number | null;
  status: 'prezent' | 'intarziat' | 'absent' | 'concediu' | 'medical' | 'liber';
  source: 'program' | 'manual' | 'corectat';
  note: string | null;
}

export interface Salary {
  daysPresent: number;
  absent: number;
  onLeave: number;
  hoursTotal: number;
  hoursNormal: number;
  hoursOvertime: number;
  hourlyRate: number;
  basePay: number;
  overtimePay: number;
  total: number;
}

export interface PayrollRow extends Salary {
  staffId: string;
  name: string;
  role: string | null;
  jobTitle: string | null;
  baseSalary: number;
}

export interface PayrollSheet {
  year: number;
  month: number;
  /** The norm and the rate the sheet was computed with — what a payslip prints. */
  dailyHours: number;
  overtimeRate: number;
  workDaysInMonth: number;
  rows: PayrollRow[];
  /** Summed on the server, where the rows are. */
  fund: number;
  overtimeFund: number;
}

/** Un fel de document de personal, din lista clinicii. */
export interface DocumentKind {
  key: string;
  label: string;
}

/** Regulile de personal ale clinicii, din Setări → Personal. */
export interface HrRules {
  leaveDaysYear: number;
  documentWarnDays: number;
  dailyHours: number;
  overtimeRate: number;
  /** Norma zilnică înmulțită cu zilele în care lucrează clinica. */
  weeklyHours: number;
  /** Felurile de documente de personal, în ordinea clinicii. */
  documentKinds: DocumentKind[];
}

export interface ExpiringDocument {
  id: string;
  staffId: string;
  staffName: string;
  kind: string;
  title: string;
  expiresAt: string | null;
}

export interface StaffDocument {
  id: string;
  staffId: string;
  kind: string;
  title: string;
  number: string | null;
  issuedAt: string | null;
  expiresAt: string | null;
  fileUrl: string | null;
  note: string | null;
}

/**
 * Written out rather than derived: validation runs with
 * `forbidNonWhitelisted`, so sending back `createdAt` would fail the request
 * instead of being ignored. `id` present means an edit.
 */
export interface SaveDocumentInput {
  id?: string;
  staffId: string;
  kind: string;
  title: string;
  number?: string | null;
  issuedAt?: string | null;
  expiresAt?: string | null;
  fileUrl?: string | null;
  note?: string | null;
}

export const hrApi = {
  leave: (params: { staffId?: string; status?: string; from?: string; to?: string } = {}) =>
    api.get<LeaveRequest[]>('/hr/leave', { query: params }),
  myLeave: () => api.get<LeaveRequest[]>('/hr/leave/mine'),
  requestLeave: (body: {
    staffId: string;
    kind: string;
    from: string;
    to: string;
    reason?: string | null;
    halfDay?: boolean;
  }) => api.post<LeaveRequest>('/hr/leave', body),
  /** `overrideBalance` aprobă peste soldul rămas; serverul cere atunci și `note`. */
  decideLeave: (id: string, approved: boolean, note?: string, overrideBalance?: boolean) =>
    api.post<{ ok: boolean }>(`/hr/leave/${id}/decide`, {
      approved,
      note,
      ...(overrideBalance ? { overrideBalance: true } : {}),
    }),
  /** Soldul fiecăruia pe un an, dintr-o singură cerere — pentru lista de aprobat. */
  leaveBalances: (year: number) =>
    api.get<Array<LeaveBalance & { staffId: string }>>('/hr/leave/balances', { query: { year } }),
  /** Retrage un concediu aprobat; zilele se eliberează în pontaj și în agendă. */
  cancelLeave: (id: string, reason?: string) =>
    api.post<{ ok: boolean }>(`/hr/leave/${id}/cancel`, { reason }),
  balance: (staffId: string, year?: number) =>
    api.get<LeaveBalance>(`/hr/leave/balance/${staffId}`, { query: { year } }),

  attendance: (params: { staffId?: string; from?: string; to?: string }) =>
    api.get<AttendanceDay[]>('/hr/attendance', { query: params }),
  saveAttendance: (body: {
    staffId: string;
    workDate: string;
    status?: string;
    hours?: number | null;
  }) => api.post<{ ok: boolean }>('/hr/attendance', body),
  /**
   * O trecere întreagă cu pensula, într-o singură cerere.
   *
   * `status: null` **șterge** ziua, nu o pune pe altă stare — e drumul înapoi
   * al anulării: o trecere peste zile goale creează rânduri, iar „cum era"
   * înseamnă acolo „nu era nimic".
   */
  saveAttendanceBulk: (days: Array<{ staffId: string; workDate: string; status: string | null }>) =>
    api.post<{ saved: number }>('/hr/attendance/bulk', { days }),
  generateMonth: (year: number, month: number) =>
    api.post<{ created: number; skipped: number }>('/hr/attendance/generate', { year, month }),

  payroll: (year: number, month: number) => api.get<PayrollSheet>(`/hr/payroll/${year}/${month}`),

  rules: () => api.get<HrRules>('/hr/rules'),

  /** Fără număr: fereastra e a clinicii, aceeași cu care numără insigna din meniu. */
  expiringDocuments: () => api.get<ExpiringDocument[]>('/hr/documents/expiring'),

  documents: (staffId?: string) =>
    api.get<StaffDocument[]>('/hr/documents', { query: { staffId } }),
  saveDocument: (body: SaveDocumentInput) => api.post<StaffDocument>('/hr/documents', body),
  /** Șterge moale; motivul, dacă e dat, rămâne pe rând și în jurnal. */
  removeDocument: (id: string, reason?: string) =>
    api.delete<void>(`/hr/documents/${id}`, { query: { reason } }),
};

export const LEAVE_KIND_LABELS = i18nMap<string>('common', {
  concediu: 'concediuDeOdihna',
  medical: 'concediuMedical',
  neplatit: 'neplatit',
  special: 'special',
});

export const LEAVE_STATUS_META = i18nMetaMap<
  string,
  { label: string; tone: 'neutral' | 'ok' | 'warn' | 'danger' }
>('common', {
  ceruta: { label: 'inAsteptare', tone: 'warn' },
  aprobata: { label: 'aprobat', tone: 'ok' },
  respinsa: { label: 'respins', tone: 'danger' },
  anulata: { label: 'anulat', tone: 'neutral' },
});

export const ATTENDANCE_LABELS = i18nMap<string>('common', {
  prezent: 'prezent',
  intarziat: 'intarziat',
  absent: 'absent',
  concediu: 'concediu',
  medical: 'medical',
  liber: 'liber',
});

// ── Onboarding, equipment and protocols ────────────────────────────────

export interface OnboardingStep {
  key: string;
  title: string;
  owner: string;
  done: boolean;
  doneAt: string | null;
  doneBy: string | null;
  note: string | null;
}

export interface StaffAsset {
  id: string;
  staffId: string;
  staffName: string | null;
  item: string;
  serial: string | null;
  issuedAt: string | null;
  returnedAt: string | null;
  note: string | null;
}

export interface Protocol {
  id: string;
  title: string;
  category: string | null;
  content: string | null;
  fileUrl: string | null;
  author: string | null;
  updatedOn: string | null;
  reviewOn: string | null;
  dueForReview: boolean;
}

export const hrExtrasApi = {
  /** Cine are lista neterminată — ca tabul să spună ceva înainte de a alege. */
  onboardingSummary: (flow: 'onboarding' | 'offboarding' = 'onboarding') =>
    api.get<Array<{ staffId: string; name: string; done: number; total: number }>>(
      '/hr/onboarding/summary',
      { query: { flow } },
    ),

  onboarding: (staffId: string, flow: 'onboarding' | 'offboarding' = 'onboarding') =>
    api.get<OnboardingStep[]>(`/hr/onboarding/${staffId}`, { query: { flow } }),

  toggleStep: (
    staffId: string,
    body: { flow: string; stepKey: string; done: boolean; note?: string | null },
  ) => api.post<OnboardingStep[]>(`/hr/onboarding/${staffId}`, body),

  assets: (query: { staffId?: string; outstandingOnly?: boolean } = {}) =>
    api.get<StaffAsset[]>('/hr/assets', {
      query: { staffId: query.staffId, outstandingOnly: query.outstandingOnly },
    }),

  saveAsset: (body: {
    staffId: string;
    item: string;
    serial?: string | null;
    note?: string | null;
  }) => api.post<{ id: string }>('/hr/assets', body),

  returnAsset: (id: string) => api.post<void>(`/hr/assets/${id}/return`),

  protocols: (query: { category?: string; dueForReview?: boolean } = {}) =>
    api.get<Protocol[]>('/hr/protocols', {
      query: { category: query.category, dueForReview: query.dueForReview },
    }),

  saveProtocol: (
    id: string | null,
    body: {
      title: string;
      category?: string | null;
      content?: string | null;
      reviewOn?: string | null;
    },
  ) =>
    id
      ? api.patch<{ id: string }>(`/hr/protocols/${id}`, body)
      : api.post<{ id: string }>('/hr/protocols', body),

  deleteProtocol: (id: string) => api.delete<void>(`/hr/protocols/${id}`),
};

// ── Shift roster ───────────────────────────────────────────────────────

export type ShiftKind = 'tura' | 'garda';

export interface Shift {
  id: string;
  staffId: string;
  /** `YYYY-MM-DD`. */
  date: string;
  /** `HH:MM`; an end before the start is a night shift. */
  start: string;
  end: string;
  kind: ShiftKind;
  branchId: string | null;
  label: string | null;
  note: string | null;
  hours: number;
}

export interface ShiftRoster {
  from: string;
  to: string;
  shifts: Shift[];
  /** Approved leave in the window — who and when only, for the clash marker. */
  leave: Array<{ staffId: string; from: string; to: string }>;
}

/** Mirrors `ShiftFieldsDto`: what an edit sends. */
export interface ShiftFieldsInput {
  start: string;
  end: string;
  kind?: ShiftKind;
  branchId?: string | null;
  label?: string | null;
  note?: string | null;
}

/** Mirrors `CreateShiftDto`. */
export interface CreateShiftInput extends ShiftFieldsInput {
  staffId: string;
  date: string;
}

export const shiftsApi = {
  roster: (query: { from: string; to: string; branchId?: string }) =>
    api.get<ShiftRoster>('/hr/shifts', { query }),

  mine: (query: { from: string; to: string }) => api.get<Shift[]>('/hr/shifts/mine', { query }),

  create: (body: CreateShiftInput) => api.post<Shift>('/hr/shifts', body),

  update: (id: string, body: ShiftFieldsInput) => api.patch<Shift>(`/hr/shifts/${id}`, body),

  remove: (id: string) => api.delete<void>(`/hr/shifts/${id}`),

  copyWeek: (body: { fromWeek: string; toWeek: string; branchId?: string }) =>
    api.post<{ created: number; skipped: number }>('/hr/shifts/copy-week', body),
};
